/** @file writeEffects: one IMPOSTOR_LAYOUT entry per live effect run, appended after the placed impostors. */
import type { Profile } from '@zilo/pose3d-format/types/profile';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import type { PoseBuffer } from '@zilo/pose3d-pose/contract';
import { nodeIndexOf } from '@zilo/pose3d-render-core/camera/nodeIndexOf';
import { viewOf, type View } from '@zilo/pose3d-render-core/camera/viewOf';
import type { Camera, ImpostorList } from '@zilo/pose3d-render-core/contract';
import { displayPivot } from '@zilo/pose3d-render-core/cut/displayPivot';
import { zBiasOf } from '@zilo/pose3d-render-core/depth/zBiasOf';
import { IMPOSTOR_LAYOUT as L } from '@zilo/pose3d-render-core/layout/impostorLayout';
import { absolutePointOf } from '@zilo/pose3d-render-core/place/absolutePointOf';
import { sheetIndexOf } from '@zilo/pose3d-render-core/place/sheetIndexOf';
import { worldPointOf } from '@zilo/pose3d-render-core/place/worldPointOf';
import { writeImpostor, type ImpostorFields } from '@zilo/pose3d-render-core/place/writeImpostor';
import { effectFrameRect } from '@zilo/pose3d-runtime-babylon/effect/effectFrameRect';
import type { EffectRun } from '@zilo/pose3d-runtime-babylon/effect/effectTypes';

/** What the effect entries of a frame are computed from (`now` in instance seconds). */
export type EffectContext = {
  runs: readonly EffectRun[]; puppet: Puppet; pose: PoseBuffer; camera: Camera; profile: Profile; entityPos: Vec3; now: number;
};

/** The entry of one run, or null when its drawable is not an effect on a known node or its strip has ended. */
function fieldsOf(run: EffectRun, ctx: EffectContext, view: View, nodes: Map<string, number>): ImpostorFields | null {
  const d = ctx.puppet.drawables[run.drawable];
  if (d?.kind !== 'effect') return null;
  const node = nodes.get(d.node);
  const tex = effectFrameRect(d.frames, ctx.now - run.started);
  if (node === undefined || tex === null) return null;
  const a = run.anchor === null
    ? absolutePointOf(worldPointOf(ctx.pose, node, d.offset, ctx.profile.zScaleRotate ?? 1), ctx.entityPos, view)
    : absolutePointOf(run.anchor, [0, 0, 0], view);
  return {
    a, b: a, pivot: displayPivot(d.pivot, tex[2], false, ctx.profile.snap, ctx.profile.halfPixel), tex, rotate: 0, scaleX: 1, scaleY: 1,
    sheer: d.sheer, zBias: zBiasOf(d.zOrder, 0), flags: 0, cutAngle: 0, cutPos: 0, cutMode: 0,
    sheet: sheetIndexOf(ctx.puppet, d.frames.sheet), drawable: run.drawable, cutOff: 0,
  };
}

/**
 * Appends one entry per live run at `count`, `count + 1`, ... (while the list has room) and returns the new count:
 * A = the run's anchor, or the entity plus the node's `worldPointOf(offset)`, rounded to the world grid as `place`
 * rounds (render-core `absolutePointOf`); B = A; pivot = `displayPivot(drawable.pivot)`; tex = the strip frame
 * (`effectFrameRect`; a finished run writes nothing); no rotation, scale 1, the drawable's sheer, `zBiasOf(zOrder, 0)`,
 * no flags and no cut; `sheet` / `drawable` indices as `place` writes them.
 */
export function writeEffects(list: ImpostorList, count: number, ctx: EffectContext): number {
  const view = viewOf(ctx.camera, ctx.profile);
  const nodes = nodeIndexOf(ctx.puppet);
  let n = count;
  for (const run of ctx.runs) {
    const fields = fieldsOf(run, ctx, view, nodes);
    if (fields !== null && (n + 1) * L.stride <= list.length) writeImpostor(list, n++, fields);
  }
  return n;
}
